import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { langOptions, langOptionsArray } from '@utils/languages';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useWindowSize } from '@hooks/useWindowSize';
import Popup from '@components/Core/Popup/Popup';
import { Button } from '@components/Core/Button';
import { Input } from '@inputs/Input/Input';
import { SelectComponent } from '@inputs/SelectComponent/SelectComponent';
import EditIcon from '@icons/inputs/edit_icon.svg?component';
import MailIcon from '@icons/inputs/mail_icon.svg?component';
import { LangType } from '@/types/common';
import { IProfileForm, ProfileFormTransFields } from './types';
import { profileFormTranslations } from './translations';
import { DeleteAccountPopup } from '@/popups/DeleteAccountPopup';
import { ChangeProfileNamePopup } from '@/popups/ChangeProfileNamePopup';
import { ChangeProfileEmailPopup } from '@/popups/ChangeProfileEmailPopup';

export const ProfileForm: FC = observer(() => {
  const {
    alertStore: { showAlert },
    profileStore: { user, changeProfile },
    popupStore: { openPopup, popupName },
    pointsStore: { setShouldUpdatePointsCategories },
  } = useStore();

  const windowSize = useWindowSize();

  const useFormMethods = useForm<IProfileForm>({
    defaultValues: {
      ...user,
    },
    reValidateMode: 'onChange',
  });

  const { setValue } = useFormMethods;

  useEffect(() => {
    if (user) {
      setValue('name', user.name);
      setValue('email', user.email);
    }
  }, [user]);

  const { mutate: changeProfileMutation, isPending } = useMutation({
    mutationFn: changeProfile,
    mutationKey: ['changeProfile'],
    onSuccess: () => {
      setShouldUpdatePointsCategories(true);
      showAlert('Your language was changed succesfully', 'success');
    },
  });

  const { t } = useMyTranslations<ProfileFormTransFields>([profileFormTranslations.name]);

  const onLangSelect = (lang: LangType) => {
    setValue('lang', lang);
    changeProfileMutation({ lang });
  };

  const maxHeight = useMemo(() => {
    return window.innerHeight - (document.getElementById('form')?.getBoundingClientRect().top || 0);
  }, [windowSize]);

  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <>
      <FormProvider {...useFormMethods}>
        <form
          id="form"
          className="flex gap-[32px] px-[48px] pb-[48px] sm-page-padding md-brand-form-right overflow-scroll"
          style={{ maxHeight }}
        >
          <section className="section">
            <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
              <h6 className="subtitle !mb-0 !uppercase">{t('Personal information')}</h6>
              <Input
                name="name"
                type="text"
                icon={<EditIcon width={24} height={24} />}
                iconPosition="right"
                readOnly
                onClick={() => openPopup('changeName')}
                iconStyleProp="!opacity-100"
                styleProp="cursor-pointer"
                label="Name"
              />
              <Input
                name="email"
                type="text"
                icon={<MailIcon width={24} height={24} />}
                iconPosition="right"
                readOnly
                onClick={() => openPopup('changeEmail')}
                iconStyleProp="!opacity-100"
                styleProp="cursor-pointer"
                label="Email"
              />
            </div>
            <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
              <h6 className="subtitle !mb-0 !uppercase">{t('Language')}</h6>
              <SelectComponent
                name="lang"
                options={langOptionsArray}
                optionLabel="name"
                optionValue="id"
                onSelect={(val) => onLangSelect(val.id)}
                customSingleValue={(val) => <p>{langOptions[val.id].name}</p>}
                isDisabled={isPending}
                isClearable={false}
              />
            </div>
            <Button
              text={t('Delete account')}
              styleType="delete"
              onClick={() => openPopup('deleteAccount')}
              styleProp="max-w-[170px] sm-brand-form-submit"
            />
          </section>
        </form>
      </FormProvider>
      <Popup>
        {popupName === 'deleteAccount' && <DeleteAccountPopup />}
        {popupName === 'changeName' && <ChangeProfileNamePopup />}
        {popupName === 'changeEmail' && <ChangeProfileEmailPopup />}
      </Popup>
    </>
  );
});
